import React from "react";
import { FormErrors } from "redux-form";

import { CommentFormData } from "@/mk2/apps/forum/components/CommentCreateEditForm";
import { BtnType } from "@/mk2/components/Btn";
import { Form } from "@/mk2/components/Form";
import { SubmitButton } from "@/mk2/components/forms/SubmitButton";
import { TextareaField } from "@/mk2/components/forms/TextareaField";
import { ComposeToolbar, FIXED, ToolbarTheme } from "@/mk2/containers/Toolbar/Toolbar";
import { ToolbarAdditionalCheckbox } from "@/mk2/containers/Toolbar/ToolbarAdditionalCheckbox";
import { MKReduxFormInjectedProps, mkReduxForm } from "@/mk2/decorators/mkReduxForm";
import {
    BLOGS_ADD_NEW_COMMENT_PLACEHOLDER,
    FORUM_COMMENT_FORM_EMPTY_MESSAGE_CANNOT_BE_SENT,
    SEND_AS_ADMIN,
    SEND_COMMENT_FORUM,
} from "@/mk/autogenerated/translation";

import styles from "./BlogPostCommentCreateForm.mscss";

interface OwnProps {
    isMobile: boolean;
    hasBlueToolbar: boolean;
    hasRating?: boolean;
    isUnauthenticatedUser?: boolean;
    initialValues: CommentFormData;
    requestUserPermissions: string[];

    textareaRef?: (r: HTMLTextAreaElement) => void;
    onSave(values: CommentFormData);
    onBlur?();
    onFocus?();
}

type Props = OwnProps;

const emojisConfig = {
    fields: ["text"],
    defaultField: "text",
};

const photosConfig = {
    photoType: "comment",
    multiple: false,
    previewPhotoSize: "s150x150",
    showPhotoCode: false,
};

class BlogPostCommentCreateFormComponent extends React.Component<
    Props & MKReduxFormInjectedProps<CommentFormData, Props>
> {
    public static validate(values: CommentFormData): FormErrors<CommentFormData> {
        const errors: FormErrors<CommentFormData> = {};
        if (
            !values.text?.trim()?.length &&
            !values.photos?.length &&
            !values.sticker &&
            !values.score
        ) {
            errors.text = FORUM_COMMENT_FORM_EMPTY_MESSAGE_CANNOT_BE_SENT;
        }

        return errors;
    }

    public render() {
        const {
            form,
            handleSubmit,
            isMobile,
            hasBlueToolbar,
            onBlur,
            onFocus,
            requestUserPermissions,
            textareaRef,
            hasRating,
            isUnauthenticatedUser,
        } = this.props;
        const isAdmin =
            !isUnauthenticatedUser && requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;
        const toolbarTheme = hasBlueToolbar ? ToolbarTheme.Blue : ToolbarTheme.Gray;

        return (
            <Form
                className={styles.BlogPostCommentCreateForm}
                onSubmit={handleSubmit}
                onFocus={onFocus}
                onBlur={onBlur}
            >
                <ComposeToolbar
                    className={styles.BlogPostCommentCreateForm__toolbar}
                    formName={form}
                    emojis={emojisConfig}
                    photos={isUnauthenticatedUser ? undefined : photosConfig}
                    isFixed={FIXED.NEVER}
                    isGray={!hasBlueToolbar}
                    isInGroups
                    isRatingVisible={hasRating && !isUnauthenticatedUser}
                    rightAction={
                        <SubmitButton
                            className={styles.BlogPostCommentCreateForm__submit}
                            canBeVisuallyDisabled={false}
                            label={SEND_COMMENT_FORUM}
                            formName={form}
                            btnType={hasBlueToolbar ? BtnType.White : BtnType.Blue}
                        />
                    }
                    textArea={
                        <div className={styles.BlogPostCommentCreateForm__textareaWrapper}>
                            <div
                                className={styles.BlogPostCommentCreateForm__textareaWrapper__lvl2}
                            >
                                <TextareaField
                                    name="text"
                                    rows={1}
                                    maxRows={isMobile ? 5 : 25}
                                    placeholder={
                                        // kvoli ios, musime vykrelist ako placeholder={} a nie ako hint={},
                                        // aby bol kurzor dobre zaindentovany
                                        BLOGS_ADD_NEW_COMMENT_PLACEHOLDER
                                    }
                                    bottomBorder={false}
                                    marginLeft={false}
                                    textareaRef={textareaRef}
                                    isCompact
                                    scrollDownOnGrow
                                />
                            </div>
                        </div>
                    }
                    additionalActions={
                        isAdmin && (
                            <ToolbarAdditionalCheckbox
                                isAdminUI
                                label={SEND_AS_ADMIN}
                                name={"asAdmin"}
                                theme={toolbarTheme}
                            />
                        )
                    }
                />
            </Form>
        );
    }
}

export const BlogPostCommentCreateFormFactory = (formName: string) =>
    mkReduxForm<CommentFormData, Props>({
        formName,
        photosConfig,
        onValidate: (values) => BlogPostCommentCreateFormComponent.validate(values),
        onSave: (values, props) => props.onSave(values),
    })(BlogPostCommentCreateFormComponent);
